import type { InputFieldDefinition, Variable } from "./types";

export type InputFieldEditorRow = InputFieldDefinition & {
  id: string;
};

export function createInputFieldEditorRow(
  field?: Partial<InputFieldDefinition>,
): InputFieldEditorRow {
  return {
    id: crypto.randomUUID(),
    key: field?.key ?? "",
    type: field?.type ?? "string",
    description: field?.description ?? "",
    source: field?.source ?? "custom",
    variableId: field?.variableId,
    sourceVariableId: field?.sourceVariableId,
    variableNameFrontend: field?.variableNameFrontend,
    variableGroup: field?.variableGroup,
    variableKind: field?.variableKind,
    variableOptions: field?.variableOptions,
    variableLinkedSchema: field?.variableLinkedSchema,
    variableLinkedTable: field?.variableLinkedTable,
    variableLinkedColumnId: field?.variableLinkedColumnId,
    variableLinkedColumnName: field?.variableLinkedColumnName,
    defaultValue: field?.defaultValue,
    configuredValue: field?.configuredValue,
    hasValue: field?.hasValue,
    required: field?.required ?? false,
    visible: field?.visible ?? true,
    order: field?.order,
    tier: field?.tier ?? "standard",
    parentVariableId: field?.parentVariableId ?? null,
  };
}

export function toInputFieldEditorRows(
  fields: InputFieldDefinition[],
): InputFieldEditorRow[] {
  return fields.map((field) => createInputFieldEditorRow(field));
}

export function variableToInputFieldDefinition(
  variable: Variable,
): InputFieldDefinition {
  return {
    key: variable.variable_name,
    type: variable.variable_type,
    description: variable.variable_description ?? undefined,
    source: "variable",
    variableId: undefined,
    sourceVariableId: variable.variable_id,
    variableNameFrontend: variable.variable_name_frontend,
    variableGroup: variable.variable_group,
    variableKind: variable.variable_kind,
    variableOptions: variable.variable_options,
    variableLinkedSchema: variable.variable_linked_schema,
    variableLinkedTable: variable.variable_linked_table,
    variableLinkedColumnId: variable.variable_linked_column_id,
    variableLinkedColumnName: variable.variable_linked_column_name,
    defaultValue: variable.variable_default,
    required: variable.variable_is_required,
    visible: variable.variable_is_visible,
    order: variable.variable_order,
    tier: variable.variable_tier,
    parentVariableId: variable.parent_variable_id,
  };
}

export function inputFieldEditorRowToDefinition(
  field: InputFieldEditorRow,
): InputFieldDefinition {
  return {
    key: field.key,
    type: field.type,
    ...(field.description ? { description: field.description } : {}),
    source: field.source ?? "custom",
    ...(field.variableId ? { variableId: field.variableId } : {}),
    ...(field.sourceVariableId
      ? { sourceVariableId: field.sourceVariableId }
      : {}),
    ...(field.variableNameFrontend !== undefined
      ? { variableNameFrontend: field.variableNameFrontend }
      : {}),
    ...(field.variableGroup !== undefined
      ? { variableGroup: field.variableGroup }
      : {}),
    ...(field.variableKind !== undefined
      ? { variableKind: field.variableKind }
      : {}),
    ...(field.variableOptions !== undefined
      ? { variableOptions: field.variableOptions }
      : {}),
    ...(field.variableLinkedSchema !== undefined
      ? { variableLinkedSchema: field.variableLinkedSchema }
      : {}),
    ...(field.variableLinkedTable !== undefined
      ? { variableLinkedTable: field.variableLinkedTable }
      : {}),
    ...(field.variableLinkedColumnId !== undefined
      ? { variableLinkedColumnId: field.variableLinkedColumnId }
      : {}),
    ...(field.variableLinkedColumnName !== undefined
      ? { variableLinkedColumnName: field.variableLinkedColumnName }
      : {}),
    ...(field.defaultValue !== undefined
      ? { defaultValue: field.defaultValue }
      : {}),
    ...(field.configuredValue !== undefined
      ? { configuredValue: field.configuredValue }
      : {}),
    ...(field.hasValue !== undefined ? { hasValue: field.hasValue } : {}),
    required: field.required ?? false,
    visible: field.visible ?? true,
    ...(field.order !== undefined ? { order: field.order } : {}),
    tier: field.tier ?? "standard",
    parentVariableId: field.parentVariableId ?? null,
  };
}

export function retainScopedChildDefinitions(
  rootFields: InputFieldDefinition[],
  scopedFields: InputFieldDefinition[],
): InputFieldDefinition[] {
  const retainedIds = new Set(
    rootFields.flatMap((field) => (field.variableId ? [field.variableId] : [])),
  );
  const children = scopedFields.filter((field) => field.parentVariableId);
  let changed = true;
  while (changed) {
    changed = false;
    for (const child of children) {
      if (
        child.variableId &&
        child.parentVariableId &&
        retainedIds.has(child.parentVariableId) &&
        !retainedIds.has(child.variableId)
      ) {
        retainedIds.add(child.variableId);
        changed = true;
      }
    }
  }
  return [
    ...rootFields,
    ...children.filter(
      (child) => child.variableId && retainedIds.has(child.variableId),
    ),
  ];
}
